import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import './i18n'; // Initialize i18n
import Landing from './pages/Landing';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiesPolicy from './pages/CookiesPolicy';
import Pricing from './pages/Pricing';
import FAQ from './pages/FAQ';
import Dashboard from './pages/Dashboard';
import Sales from './pages/Sales';
import Bookings from './pages/Bookings';
import Expenses from './pages/Expenses';
import Documents from './pages/Documents';
import Archived from './pages/Archived';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import TrialExpired from './pages/TrialExpired';
import Support from './pages/Support';
import Invoices from './pages/Invoices';
import Help from './pages/Help';
import HotelComparison from './pages/HotelComparison';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import Layout from './components/Layout';
import PrivacyConsentBanner from './components/PrivacyConsentBanner';
import SEOHead from './components/SEOHead';
import { TeamPermissionsProvider } from './contexts/TeamPermissionsContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Axios interceptor for handling trial expiration
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('Trial expired')) {
      window.location.href = '/trial-expired';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const adminToken = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
    }
    if (adminToken) {
      setIsAdmin(true);
    }
    setLoading(false);
  }, []);

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="text-lg text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <HelmetProvider>
    <div className="App">
      <SEOHead />
      <BrowserRouter>
        <PrivacyConsentBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/trial-expired" element={<TrialExpired />} />
          <Route path="/support" element={<Support />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/app/admin" element={<AdminLogin setIsAdmin={setIsAdmin} />} />
          <Route
            path="/app/admin/dashboard"
            element={isAdmin ? <AdminDashboard /> : <Navigate to="/app/admin" />}
          />
          <Route
            path="/app/admin/settings"
            element={isAdmin ? <AdminSettings /> : <Navigate to="/app/admin" />}
          />
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/app/dashboard" />
              ) : (
                <Login setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />
          <Route
            path="/app"
            element={
              isAuthenticated ? (
                <TeamPermissionsProvider>
                  <Layout setIsAuthenticated={setIsAuthenticated} />
                </TeamPermissionsProvider>
              ) : (
                <Navigate to="/login" />
              )
            }
          >
            <Route index element={<Navigate to="/app/dashboard" />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="sales" element={<Sales />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="expenses" element={<Expenses />} />
            <Route path="documents" element={<Documents />} />
            <Route path="help" element={<Help />} />
            <Route path="archived" element={<Archived />} />
            <Route path="settings" element={<Settings />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="hotels" element={<HotelComparison />} />
          </Route>
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
    </HelmetProvider>
  );
}

export default App;
